/**
 * Knowledge Management Hub - Main Interface
 * Complete end-to-end knowledge base management with simple user workflows
 * Integrates upload, processing, vector store management, and agent integration
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { 
  Upload, Database, Brain, BarChart3, 
  ArrowRight, CheckCircle2, Zap, Users, Search
} from 'lucide-react';
import EnhancedKnowledgeUploader from './EnhancedKnowledgeUploader';
import VectorStoreManager from './VectorStoreManager';
import AgentKnowledgeIntegration from './AgentKnowledgeIntegration';
import RAGQueryBuilder, { FacetFilters } from './RAGQueryBuilder';
import ConfidenceBadge from './ConfidenceBadge';
import { searchKnowledgeBase, SearchResult } from '../../services/ragService';

interface KnowledgeManagementHubProps {
  organizationId: string;
  userId?: string;
}

export const KnowledgeManagementHub: React.FC<KnowledgeManagementHubProps> = ({
  organizationId,
  userId
}) => {
  const [activeView, setActiveView] = useState<'home' | 'upload' | 'vector-store' | 'agent-integration' | 'search'>('home');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const handleSearch = async (query: string, facets: FacetFilters) => {
    setIsSearching(true);
    setSearchError(null);
    try {
      const response = await searchKnowledgeBase(query, facets);
      setSearchResults(response.results);
    } catch (error) {
      setSearchError(error instanceof Error ? error.message : 'An error occurred while searching');
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const renderSearchResults = () => (
    <div className="space-y-4">
      {searchError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {searchError}
        </div>
      )}
      
      {searchResults.length > 0 && (
        <div className="text-sm text-gray-600 mb-2">
          Found {searchResults.length} results
        </div>
      )}
      
      <div className="space-y-4">
        {searchResults.map((result, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <div className="space-y-3">
                {/* Document Content */}
                <div className="text-gray-900">{result.document}</div>
                
                {/* Confidence Badges */}
                {result.metadata?.classifications && (
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(result.metadata.classifications).map(([facet, classification]) => (
                      classification && typeof classification === 'object' && 'confidence' in classification && (
                        <div key={facet} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 capitalize">
                            {facet.replace(/_/g, ' ')}:
                          </span>
                          <ConfidenceBadge 
                            confidence={classification.confidence} 
                            size="sm"
                          />
                          <span className="text-xs text-gray-700">
                            {classification.value}
                          </span>
                        </div>
                      )
                    ))}
                  </div>
                )}
                
                {/* Distance Score */}
                <div className="text-xs text-gray-500">
                  Relevance: {(1 - result.distance).toFixed(2)}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      
      {!isSearching && searchResults.length === 0 && !searchError && (
        <div className="text-center py-12 text-gray-500">
          No results found. Try adjusting your search query or filters.
        </div>
      )}
    </div>
  );

  const renderHomeDashboard = () => (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg">
        <Brain className="h-16 w-16 text-blue-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Knowledge Base Manager</h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Upload documents, create vector embeddings, and enable AI agents to generate 
          knowledge-enhanced content for your organization.
        </p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6 text-center">
            <Upload className="h-8 w-8 text-blue-600 mx-auto mb-3" />
            <div className="text-2xl font-bold text-gray-900">127</div>
            <div className="text-sm text-gray-600">Documents Processed</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 text-center">
            <Database className="h-8 w-8 text-green-600 mx-auto mb-3" />
            <div className="text-2xl font-bold text-gray-900">15.4K</div>
            <div className="text-sm text-gray-600">Vector Embeddings</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 text-center">
            <Brain className="h-8 w-8 text-purple-600 mx-auto mb-3" />
            <div className="text-2xl font-bold text-gray-900">8</div>
            <div className="text-sm text-gray-600">Connected Agents</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 text-center">
            <Zap className="h-8 w-8 text-orange-600 mx-auto mb-3" />
            <div className="text-2xl font-bold text-gray-900">+47%</div>
            <div className="text-sm text-gray-600">Content Quality Boost</div>
          </CardContent>
        </Card>
      </div>

      {/* Workflow Steps */}
      <div className="bg-white rounded-lg p-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Simple 3-Step Workflow</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Upload className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Upload Documents</h3>
            <p className="text-gray-600 text-sm">
              Drag & drop your PDFs, Word docs, presentations, and more. 
              Automatic text extraction and smart chunking.
            </p>
            <Button 
              className="mt-4" 
              variant="secondary"
              onClick={() => setActiveView('upload')}
            >
              Start Upload
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          <div className="text-center">
            <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Database className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Vector Processing</h3>
            <p className="text-gray-600 text-sm">
              Automatic OpenAI embeddings generation and ChromaDB storage. 
              Monitor processing and manage vector collections.
            </p>
            <Button 
              className="mt-4" 
              variant="secondary"
              onClick={() => setActiveView('vector-store')}
            >
              Manage Vectors
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>

          <div className="text-center">
            <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <Brain className="h-8 w-8 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Agent Integration</h3>
            <p className="text-gray-600 text-sm">
              AI agents automatically access your knowledge base to generate 
              enhanced, contextual content.
            </p>
            <Button 
              className="mt-4" 
              variant="secondary"
              onClick={() => setActiveView('agent-integration')}
            >
              Test Agents
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <div>
                  <div className="font-medium">Product_Guide_2024.pdf processed successfully</div>
                  <div className="text-sm text-gray-600">Generated 45 chunks, 69,120 embeddings</div>
                </div>
              </div>
              <Badge className="bg-green-100 text-green-800">Completed</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Brain className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-medium">Casey Email Agent generated enhanced content</div>
                  <div className="text-sm text-gray-600">Used product knowledge for personalized messaging</div>
                </div>
              </div>
              <Badge className="bg-blue-100 text-blue-800">Generated</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Database className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="font-medium">Vector store optimization completed</div>
                  <div className="text-sm text-gray-600">Improved query performance by 23%</div>
                </div>
              </div>
              <Badge className="bg-purple-100 text-purple-800">Optimized</Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <div className="flex justify-center gap-4">
        <Button onClick={() => setActiveView('upload')}>
          <Upload className="h-4 w-4 mr-2" />
          Upload Documents
        </Button>
        <Button variant="secondary" onClick={() => setActiveView('agent-integration')}>
          <Users className="h-4 w-4 mr-2" />
          Test Agent Enhancement
        </Button>
        <Button variant="secondary" onClick={() => setActiveView('vector-store')}>
          <BarChart3 className="h-4 w-4 mr-2" />
          View Analytics
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-6">
            <Button 
              variant={activeView === 'home' ? 'default' : 'secondary'}
              onClick={() => setActiveView('home')}
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Dashboard
            </Button>
            <Button 
              variant={activeView === 'search' ? 'default' : 'secondary'}
              onClick={() => setActiveView('search')}
            >
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
            <Button 
              variant={activeView === 'upload' ? 'default' : 'secondary'}
              onClick={() => setActiveView('upload')}
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload & Process
            </Button>
            <Button 
              variant={activeView === 'vector-store' ? 'default' : 'secondary'}
              onClick={() => setActiveView('vector-store')}
            >
              <Database className="h-4 w-4 mr-2" />
              Vector Store
            </Button>
            <Button 
              variant={activeView === 'agent-integration' ? 'default' : 'secondary'}
              onClick={() => setActiveView('agent-integration')}
            >
              <Brain className="h-4 w-4 mr-2" />
              Agent Integration
            </Button>
          </div>
          
          <div className="text-sm text-gray-600">
            Organization: <span className="font-medium">{organizationId}</span>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-sm">
          {activeView === 'home' && renderHomeDashboard()}
          {activeView === 'search' && (
            <div className="p-6">
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Search Knowledge Base</h2>
                <p className="text-gray-600">
                  Filter and search through your organization's knowledge base with advanced classification facets.
                </p>
              </div>
              <RAGQueryBuilder 
                onSearch={handleSearch} 
                isLoading={isSearching}
              />
              <div className="mt-6">
                {renderSearchResults()}
              </div>
            </div>
          )}
          {activeView === 'upload' && (
            <div className="p-6">
              <EnhancedKnowledgeUploader 
                organizationId={organizationId}
                onDocumentProcessed={(doc) => {
                  console.log('Document processed:', doc);
                }}
              />
            </div>
          )}
          {activeView === 'vector-store' && (
            <div className="p-6">
              <VectorStoreManager 
                organizationId={organizationId}
                onCollectionSelect={(collection) => {
                  console.log('Collection selected:', collection);
                }}
              />
            </div>
          )}
          {activeView === 'agent-integration' && (
            <div className="p-6">
              <AgentKnowledgeIntegration 
                organizationId={organizationId}
                onTestComplete={(test) => {
                  console.log('Test completed:', test);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeManagementHub;